/* ═══════════════════════════════════════════════════════════════════════════
   🖼 services/mediaAspect.js — the box a photo or a clip gets, before it lands.

   ══ WHY THIS EXISTS ═══════════════════════════════════════════════════════
   🟥 "IT SHOWS THE IMAGE SMALL AND THEN FLICKERS TO SHOW IT VERTICALLY." A
      single photo bubble is given an EXACT pixel box so the message list does
      not jump while the bytes are still moving — and that box is computed from
      `width` / `height` on the message document. When those are missing the
      fallback is `maxW × 0.72`, which is a LANDSCAPE box: a vertical photo is
      therefore drawn wide and squat, and snaps upright the moment it decodes.

   🟥 AND THE MISSING NUMBERS WERE NOT AN ACCIDENT OF AGE. Every file sent to
      the Allo account went through a sanitiser that kept four fields — kind,
      url, name, mime — so a photo's shape was deleted on the way in, by a
      function whose job was to check that a url began with https. New messages
      carry it now; the ones already sent never will.

   🟢 SO THE SHAPE IS LEARNED AND REMEMBERED. The first time a photo decodes,
      its natural size is recorded against the message id; every later render —
      scrolling it back into view, re-opening the chat — reserves the right box
      on the first frame. The phone's bubble has done exactly this for VIDEO
      since long before this file existed (`videoAspectCache` in
      MessageBubble.tsx); this is that idea, for the browser, for both.

   🟥 IN MEMORY, NOT IN STORAGE, AND DELIBERATELY. What is cached here is a
      derived fact about bytes the browser has already fetched — worth keeping
      for as long as the tab lives, and worth nothing afterwards, because a
      reload re-reads the message document that now carries the real numbers.
   ═══════════════════════════════════════════════════════════════════════════ */

const clamp = (lo, v, hi) => Math.max(lo, Math.min(v, hi));

/** message id → width / height, learned from a decode. */
const _aspect = new Map();

/**
 * Remember what a photo or clip actually turned out to be.
 *
 * 🟢 THE FIRST ANSWER WINS. A <video> reports `videoWidth` as 0 until it has
 *    metadata and an <img> can fire on a broken decode; refusing zero and never
 *    overwriting a good value keeps one bad event from resizing a correct box.
 */
export function rememberAspect(id, w, h) {
  const key = String(id || '');
  const width = Number(w) || 0;
  const height = Number(h) || 0;
  if (!key || width <= 0 || height <= 0) return null;
  const a = width / height;
  if (!Number.isFinite(a) || a <= 0) return null;
  if (!_aspect.has(key)) _aspect.set(key, a);
  return _aspect.get(key);
}

/** What we learned last time, or null. */
export function recallAspect(id) {
  const key = String(id || '');
  return key && _aspect.has(key) ? _aspect.get(key) : null;
}

/**
 * The exact pixel box for one photo or clip.
 *
 * 🟢 COMPUTED, NOT CSS-GUESSED, so the bubble reserves the right space BEFORE
 *    the bytes land and the list never jumps.
 *
 * @param w,h    the sender's dimensions, when the message carries them
 * @param vp     the viewport, so the cap follows the window
 * @param aspect what we learned from a previous decode, when it does not
 *
 * 🟥 THE LEARNED ASPECT IS THE FALLBACK, NOT AN OVERRIDE. The sender's numbers
 *    describe the ORIGINAL; a re-encoded copy this browser happens to have
 *    decoded could disagree, and every other client is drawing the original.
 */
export function fitBox(w, h, vp, aspect) {
  const maxW = clamp(240, vp.w * 0.34, 560);
  const maxH = clamp(220, vp.h * 0.58, 640);
  if (!w || !h) {
    /* 🟥 THIS IS WHERE THE FLICKER WAS. With nothing to go on, the box was
       `maxW × 0.72` — landscape — so a vertical photo drew wide and squat and
       then snapped upright. A remembered shape is worth far more than a guess,
       and one that IS a guess should at least be square rather than confidently
       the wrong way round. */
    if (Number.isFinite(aspect) && aspect > 0) {
      const s = Math.min(maxW, maxH * aspect);
      return { width: Math.round(s), height: Math.round(s / aspect) };
    }
    const side = Math.round(Math.min(maxW, maxH * 0.9));
    return { width: side, height: Math.round(side * 0.9) };
  }
  const s = Math.min(maxW / w, maxH / h);
  return { width: Math.round(w * s), height: Math.round(h * s) };
}

/** Test seam: forget everything. Never called by the app. */
export function _resetAspects() { _aspect.clear(); }
